// Token refresh transport + outcome classification (used by lib/api.ts).
//
// Offline Safety: a refresh that fails because of the NETWORK (no connection,
// abort, timeout) must NOT log the cashier out — the POS keeps working
// offline and the refresh is retried when the connection returns. Only a real
// auth rejection logs out:
//   400 / 401 / 403 from /auth/refresh  → rejected (refresh token unusable)
//   2xx with an invalid body            → rejected
//   network error / timeout             → throws RefreshNetworkError (a TypeError,
//                                         so callers treat it like any network failure)
//   408 / 429 / 5xx                     → throws RefreshTransientError (server trouble)

export const REFRESH_TIMEOUT_MS = 15_000;

/** Network failure / timeout while refreshing. Extends TypeError on purpose: fetch network errors are TypeErrors. */
export class RefreshNetworkError extends TypeError {
  readonly timedOut: boolean;
  constructor(timedOut: boolean, cause?: unknown) {
    super(
      timedOut
        ? "La renovación de la sesión tardó demasiado (sin conexión estable)."
        : "Sin conexión: no se pudo renovar la sesión.",
    );
    this.name = "RefreshNetworkError";
    this.timedOut = timedOut;
    (this as any).cause = cause;
  }
}

/** The refresh endpoint answered with a transient server error — not an auth rejection. */
export class RefreshTransientError extends Error {
  readonly status: number;
  readonly response: { status: number; data: unknown };
  constructor(status: number, data?: unknown) {
    super("El servidor no pudo renovar la sesión en este momento. Intenta de nuevo en unos segundos.");
    this.name = "RefreshTransientError";
    this.status = status;
    this.response = { status, data };
  }
}

/** kind "ok" carries both tokens; kind "rejected" means the session is really over. */
export interface RefreshOutcome {
  kind: "ok" | "rejected";
  accessToken?: string;
  refreshToken?: string;
  status?: number;
  reason?: string;
}

const AUTH_REJECTION_STATUSES = new Set([400, 401, 403]);

export async function requestTokenRefresh(options: {
  url: string;
  refreshToken: string;
  tenantId?: string | null;
  timeoutMs?: number;
  fetchImpl?: typeof fetch;
}): Promise<RefreshOutcome> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const timeoutMs = options.timeoutMs ?? REFRESH_TIMEOUT_MS;
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  const headers = new Headers({ "Content-Type": "application/json" });
  if (options.tenantId) headers.set("x-tenant-id", options.tenantId);

  try {
    let response: Response;
    try {
      response = await fetchImpl(options.url, {
        method: "POST",
        headers,
        body: JSON.stringify({ refreshToken: options.refreshToken }),
        signal: controller.signal,
      });
    } catch (err) {
      // TypeError (no network), AbortError (our timeout) — never an auth verdict.
      throw new RefreshNetworkError(timedOut, err);
    }

    if (AUTH_REJECTION_STATUSES.has(response.status)) {
      return { kind: "rejected", status: response.status, reason: `HTTP ${response.status}` };
    }
    if (!response.ok) {
      let data: unknown;
      try {
        data = await response.json();
      } catch {
        data = undefined;
      }
      throw new RefreshTransientError(response.status, data);
    }

    let text: string;
    try {
      text = await response.text();
    } catch (err) {
      throw new RefreshNetworkError(timedOut, err); // connection dropped mid-body
    }
    let data: any;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      return { kind: "rejected", status: response.status, reason: "Invalid refresh response" };
    }
    if (!data || typeof data.accessToken !== "string" || !data.accessToken || typeof data.refreshToken !== "string" || !data.refreshToken) {
      return { kind: "rejected", status: response.status, reason: "Invalid refresh response" };
    }
    return { kind: "ok", accessToken: data.accessToken, refreshToken: data.refreshToken };
  } finally {
    clearTimeout(timer);
  }
}

/** Network or transient server failure while refreshing (the session is NOT over). */
export const isRefreshTransientFailure = (err: unknown): boolean =>
  err instanceof RefreshNetworkError || err instanceof RefreshTransientError;
